import "../style/dinner-suggestions-popup.scss"

import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDispatch, useSelector, useStore } from "react-redux"
import { LocalStorage } from "@ng-mw/shared-react-components"
import { consentsActions } from "@ng-mw/shared-react-components/consents"
import Alert from "@ng-mw/reol/components/Alert"
import { CloseButton, ModalBodyText } from "@ng-mw/reol/components/Modal"

import { hideDinnerSuggestionsPopup } from "@app/store/app/appActions"
import MenyLocalStorage from "@app/MenyLocalStorage"
import { RootState } from "@app/types"
import Button from "@ng-mw/reol/components/Button"
import ButtonGroup from "@ng-mw/reol/components/ButtonGroup"
import {
    getAnalyticalConsent,
    getAnalyticalConsentStatus,
} from "../helpers/getAnalyticalConsentStatus"
import DrawerPopup from "@modules/popup/DrawerPopup"
import { PopupButtons } from "@modules/popup/Popup"
import PopupContent from "@modules/popup/PopupContent"
import {
    dinnerSuggestionsFlowReducer,
    DinnerSuggestionsPreferenceSelections,
    getDinnerSuggestionsHeaderTitle,
    getDinnerSuggestionsCanContinue,
    getDinnerSuggestionsPrimaryButtonText,
    getDinnerSuggestionsStepIndex,
    initialDinnerSuggestionsFlowState,
} from "./DinnerSuggestionsPopup.flow"
import {
    DinnerSuggestionsConsentStep,
    DinnerSuggestionsImportantStep,
    DinnerSuggestionsIntroStep,
    DinnerSuggestionsMoreOfStep,
} from "./DinnerSuggestionsPopupSteps"
import { saveDinnerSuggestionsPreferences } from "../preferences/DinnerSuggestionsPreferenceApi"
import type {
    DinnerSuggestionsImportantToMeOptionId,
    DinnerSuggestionsMoreOfOptionId,
} from "../preferences/types"
import {
    dinnerSuggestionsImportantToMeOptions,
    dinnerSuggestionsMoreOfOptions,
} from "../preferences/options"
import { DinnerSuggestionSubject, TrackingEventPage, UserAction } from "@app/types/GtmData"
import useTrackDinnerSuggestionsEvent from "../useTrackDinnerSuggestionsEvent"
import {
    buildDinnerSuggestionsTrackingAction,
    getDinnerSuggestionsPrimaryTrackingAction,
    getDinnerSuggestionsSecondaryTrackingAction,
} from "./DinnerSuggestionsPopup.tracking"

const dinnerSuggestionsPreferenceErrorMessage =
    "Kunne ikke lagre valgene dine akkurat nå. Prøv igjen."
const dinnerSuggestionsConsentErrorMessage =
    "Kunne ikke lagre samtykket ditt akkurat nå. Prøv igjen."
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect

interface DinnerSuggestionsPreferences {
    isCompleted?: boolean
    completedAt?: string
    moreOf?: DinnerSuggestionsMoreOfOptionId[]
    importantToMe?: DinnerSuggestionsImportantToMeOptionId[]
}

const DinnerSuggestionsPopup = () => {
    const dispatch = useDispatch<any>()
    const store = useStore<RootState>()
    const isLoggedIn = useSelector((state: RootState) => state.user.loginLevel > 0)
    const { visible, startStep, mode } = useSelector(
        (state: RootState) => state.app.dinnerSuggestionsPopup,
    )
    const consents = useSelector((state: RootState) => state.consents)
    const trackDinnerSuggestionsEvent = useTrackDinnerSuggestionsEvent()
    const [flowState, flowDispatch] = useReducer(
        dinnerSuggestionsFlowReducer,
        initialDinnerSuggestionsFlowState,
    )
    const [declineModalIsVisible, setDeclineModalIsVisible] = useState(false)
    const [confirmationHost, setConfirmationHost] = useState<HTMLElement | null>(null)
    const [analyticalConsentSelected, setAnalyticalConsentSelected] = useState<boolean | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const confirmationReturnFocusRef = useRef<HTMLElement | null>(null)

    const currentStepIndex = getDinnerSuggestionsStepIndex(flowState.currentStep)
    const isConsentOnlyFlow = mode === "consent-only"
    const isInfoOnlyFlow = mode === "info-only"
    const trackingEventPage = isConsentOnlyFlow
        ? TrackingEventPage.ConsentPopup
        : isInfoOnlyFlow
        ? TrackingEventPage.InfoPopup
        : TrackingEventPage.DinnerSuggestionOnboarding
    const canGoBack = currentStepIndex > 0 && !isConsentOnlyFlow
    const canContinue = getDinnerSuggestionsCanContinue(flowState)
    const headerTitle = getDinnerSuggestionsHeaderTitle(flowState.currentStep)
    const showHeaderCloseButton = canGoBack || isConsentOnlyFlow
    const hasAnalyticalConsent = getAnalyticalConsentStatus(consents.data)
    const shouldShowConsentStep = !consents.consentsInitialized || !hasAnalyticalConsent
    const isAnalyticalConsentSelected = analyticalConsentSelected ?? hasAnalyticalConsent
    const isConsentStepPending =
        flowState.currentStep === "consent" && !consents.consentsInitialized
    const isSkippableStep =
        flowState.currentStep === "moreOf" || flowState.currentStep === "importantToMe"
    const primaryButtonText = getDinnerSuggestionsPrimaryButtonText(
        flowState.currentStep,
        flowState.submitting,
        shouldShowConsentStep,
    )
    const secondaryButtonText = isSkippableStep ? "Hopp over" : "Lukk"

    const persistPreferences = (preferences: DinnerSuggestionsPreferenceSelections = flowState) => {
        const storedPreferences = LocalStorage.get(
            MenyLocalStorage.DinnerSuggestionsOnboarding,
        ) as DinnerSuggestionsPreferences | null

        LocalStorage.set(MenyLocalStorage.DinnerSuggestionsOnboarding, {
            ...storedPreferences,
            moreOf: preferences.moreOf,
            importantToMe: preferences.importantToMe,
        })
    }

    const completeOnboarding = () => {
        LocalStorage.set(MenyLocalStorage.DinnerSuggestionsOnboarding, {
            ...(LocalStorage.get(MenyLocalStorage.DinnerSuggestionsOnboarding) || {}),
            isCompleted: true,
            completedAt: new Date().toISOString(),
        })
        handleClose()
    }

    const handleClose = () => {
        if (flowState.submitting) return

        setDeclineModalIsVisible(false)
        dispatch(hideDinnerSuggestionsPopup())
    }

    const handleGoBack = () => {
        if (flowState.submitting) return

        trackDinnerSuggestionsEvent({
            action: buildDinnerSuggestionsTrackingAction(
                UserAction.ClickOn,
                DinnerSuggestionSubject.Back,
            ),
            eventPage: trackingEventPage,
        })
        setSubmitError(null)
        flowDispatch({ type: "GO_TO_PREVIOUS_STEP" })
    }

    const handleToggleMoreOf = (value: DinnerSuggestionsMoreOfOptionId) => {
        const option = dinnerSuggestionsMoreOfOptions.find(item => item.id === value)

        trackDinnerSuggestionsEvent({
            action: buildDinnerSuggestionsTrackingAction(
                flowState.moreOf.includes(value) ? UserAction.TurnOff : UserAction.TurnOn,
                option?.label || value,
            ),
            eventPage: trackingEventPage,
        })
        flowDispatch({ type: "TOGGLE_MORE_OF", value })
    }

    const handleToggleImportantToMe = (value: DinnerSuggestionsImportantToMeOptionId) => {
        const option = dinnerSuggestionsImportantToMeOptions.find(item => item.id === value)

        trackDinnerSuggestionsEvent({
            action: buildDinnerSuggestionsTrackingAction(
                flowState.importantToMe.includes(value) ? UserAction.TurnOff : UserAction.TurnOn,
                option?.label || value,
            ),
            eventPage: trackingEventPage,
        })
        flowDispatch({ type: "TOGGLE_IMPORTANT_TO_ME", value })
    }

    const handleToggleAnalyticalConsent = () => {
        if (isConsentStepPending || flowState.submitting || declineModalIsVisible) return

        setSubmitError(null)
        setAnalyticalConsentSelected(!isAnalyticalConsentSelected)
    }

    const savePreferences = async () => {
        await saveDinnerSuggestionsPreferences({
            moreOf: flowState.moreOf,
            importantToMe: flowState.importantToMe,
        })
        persistPreferences()
    }

    const savePreferencesAndContinue = async () => {
        flowDispatch({ type: "SET_SUBMITTING", value: true })
        setSubmitError(null)

        try {
            await savePreferences()

            if (shouldShowConsentStep) {
                flowDispatch({ type: "GO_TO_NEXT_STEP" })
            } else {
                completeOnboarding()
            }
        } catch {
            setSubmitError(dinnerSuggestionsPreferenceErrorMessage)
        } finally {
            flowDispatch({ type: "SET_SUBMITTING", value: false })
        }
    }

    const handlePrimaryAction = async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!canContinue || flowState.submitting) return

        trackDinnerSuggestionsEvent({
            action: getDinnerSuggestionsPrimaryTrackingAction(
                flowState.currentStep,
                shouldShowConsentStep,
            ),
            eventPage: trackingEventPage,
        })

        if (flowState.currentStep === "importantToMe") {
            await savePreferencesAndContinue()
            return
        }

        if (flowState.currentStep === "consent") {
            if (!isAnalyticalConsentSelected) {
                confirmationReturnFocusRef.current = event.currentTarget
                setConfirmationHost(
                    event.currentTarget.closest<HTMLElement>(".ngr-modal-drawer__box"),
                )
                setDeclineModalIsVisible(true)
                return
            }

            setSubmitError(null)
            flowDispatch({ type: "SET_SUBMITTING", value: true })

            try {
                const analyticalConsent = getAnalyticalConsent(consents.data)

                if (!analyticalConsent) {
                    setSubmitError(dinnerSuggestionsConsentErrorMessage)
                    return
                }

                if (analyticalConsent.status !== "true") {
                    await dispatch(
                        consentsActions.updateConsentStatus(analyticalConsent, "true", {}),
                    )

                    const updatedAnalyticalConsent = getAnalyticalConsent(
                        store.getState().consents.data,
                    )

                    if (updatedAnalyticalConsent?.status !== "true") {
                        setSubmitError(dinnerSuggestionsConsentErrorMessage)
                        return
                    }

                    trackDinnerSuggestionsEvent({
                        action: buildDinnerSuggestionsTrackingAction(
                            UserAction.TurnOn,
                            DinnerSuggestionSubject.AnalyticalConsent,
                        ),
                        eventPage: trackingEventPage,
                    })
                }

                if (isConsentOnlyFlow) {
                    handleClose()
                    return
                }

                completeOnboarding()
            } catch {
                setSubmitError(dinnerSuggestionsConsentErrorMessage)
            } finally {
                flowDispatch({ type: "SET_SUBMITTING", value: false })
            }
            return
        }

        flowDispatch({ type: "GO_TO_NEXT_STEP" })
    }

    const handleSecondaryAction = async () => {
        if (flowState.submitting) return

        trackDinnerSuggestionsEvent({
            action: getDinnerSuggestionsSecondaryTrackingAction(flowState.currentStep),
            eventPage: trackingEventPage,
        })

        if (isSkippableStep) {
            flowDispatch({ type: "SKIP_CURRENT_STEP" })

            if (flowState.currentStep === "importantToMe" && !shouldShowConsentStep) {
                handleClose()
            }

            return
        }

        handleClose()
    }

    const handleDismissDeclineModal = () => {
        if (flowState.submitting) return

        trackDinnerSuggestionsEvent({
            action: buildDinnerSuggestionsTrackingAction(
                UserAction.ClickOn,
                DinnerSuggestionSubject.IntroDialog,
            ),
            eventPage: trackingEventPage,
        })
        setDeclineModalIsVisible(false)
    }

    const handleContinueWithoutConsent = async () => {
        if (flowState.submitting) return

        trackDinnerSuggestionsEvent({
            action: buildDinnerSuggestionsTrackingAction(
                UserAction.ClickOn,
                DinnerSuggestionSubject.GoToRecipes,
            ),
            eventPage: trackingEventPage,
        })

        const analyticalConsent = getAnalyticalConsent(store.getState().consents.data)

        if (analyticalConsent?.status === "true") {
            flowDispatch({ type: "SET_SUBMITTING", value: true })
            setSubmitError(null)

            try {
                await dispatch(consentsActions.updateConsentStatus(analyticalConsent, "false", {}))

                if (getAnalyticalConsent(store.getState().consents.data)?.status !== "false") {
                    setSubmitError(dinnerSuggestionsConsentErrorMessage)
                    setDeclineModalIsVisible(false)
                    return
                }

                trackDinnerSuggestionsEvent({
                    action: buildDinnerSuggestionsTrackingAction(
                        UserAction.TurnOff,
                        DinnerSuggestionSubject.AnalyticalConsent,
                    ),
                    eventPage: trackingEventPage,
                })
            } catch {
                setSubmitError(dinnerSuggestionsConsentErrorMessage)
                setDeclineModalIsVisible(false)
                return
            } finally {
                flowDispatch({ type: "SET_SUBMITTING", value: false })
            }
        }

        if (isConsentOnlyFlow) {
            handleClose()
        } else {
            completeOnboarding()
        }
    }

    useEffect(() => {
        if (visible) {
            setSubmitError(null)
            const storedPreferences = LocalStorage.get(
                MenyLocalStorage.DinnerSuggestionsOnboarding,
            ) as DinnerSuggestionsPreferences | null

            flowDispatch({
                type: "INITIALIZE_PREFERENCES",
                value: {
                    moreOf: storedPreferences?.moreOf || [],
                    importantToMe: storedPreferences?.importantToMe || [],
                },
            })
        }
    }, [visible])

    useEffect(() => {
        if (visible && isLoggedIn && !consents.consentsInitialized && !consents.loading) {
            dispatch(consentsActions.fetchConsentsAndAgreements())
        }
    }, [consents.consentsInitialized, consents.loading, dispatch, isLoggedIn, visible])

    useEffect(() => {
        if (!visible) {
            setDeclineModalIsVisible(false)
            setConfirmationHost(null)
            setAnalyticalConsentSelected(null)
        }
    }, [visible])

    useIsomorphicLayoutEffect(() => {
        if (!declineModalIsVisible || !confirmationHost) {
            if (visible) confirmationReturnFocusRef.current?.focus()
            confirmationReturnFocusRef.current = null
            return
        }

        const drawer = confirmationHost.querySelector<HTMLElement>(
            ".dinner-suggestions-popup-modal--consent",
        )
        const drawerDialog = confirmationHost.closest("dialog")

        drawer?.setAttribute("inert", "")
        drawerDialog?.classList.add("dinner-suggestions-popup-dialog--confirmation-visible")
        confirmationHost
            .querySelector<HTMLButtonElement>(".dinner-suggestions-decline-popup__close")
            ?.focus()

        return () => {
            drawer?.removeAttribute("inert")
            drawerDialog?.classList.remove("dinner-suggestions-popup-dialog--confirmation-visible")
        }
    }, [confirmationHost, declineModalIsVisible, visible])

    useEffect(() => {
        if (visible && startStep) {
            flowDispatch({ type: "GO_TO_STEP", value: startStep })
        }
    }, [visible, startStep])

    const renderStep = () => {
        switch (flowState.currentStep) {
            case "intro":
                return (
                    <>
                        <img
                            className="dinner-suggestions-popup__image"
                            src="https://cdn.sanity.io/images/2vehtkn0/production/1bcedec8570a5355ec543d4fb6df1be2d341deed-3610x2710.png?auto=format&fit=max&q=70&w=740&h=440"
                            loading="lazy"
                            aria-hidden="true"
                            alt="Middagstretter med reinsdyrkjøtt"
                        />
                        <PopupContent className="dinner-suggestions-popup__content">
                            <DinnerSuggestionsIntroStep isInfoOnlyFlow={isInfoOnlyFlow} />
                        </PopupContent>
                    </>
                )
            case "moreOf":
                return (
                    <PopupContent className="dinner-suggestions-popup__content">
                        <DinnerSuggestionsMoreOfStep
                            selectedValues={flowState.moreOf}
                            onToggle={handleToggleMoreOf}
                        />
                    </PopupContent>
                )
            case "importantToMe":
                return (
                    <PopupContent className="dinner-suggestions-popup__content">
                        <DinnerSuggestionsImportantStep
                            selectedValues={flowState.importantToMe}
                            onToggle={handleToggleImportantToMe}
                        />
                    </PopupContent>
                )
            case "consent":
                return (
                    <PopupContent className="dinner-suggestions-popup__content">
                        <DinnerSuggestionsConsentStep
                            key={`${visible}-${consents.consentsInitialized}-${hasAnalyticalConsent}`}
                            className="dinner-suggestions-popup__consent-screen"
                            description={getAnalyticalConsent(consents.data)?.description}
                            isChecked={isAnalyticalConsentSelected}
                            isDisabled={isConsentStepPending || flowState.submitting}
                            onToggle={handleToggleAnalyticalConsent}
                        />
                    </PopupContent>
                )
            default:
                return null
        }
    }

    return (
        <>
            <DrawerPopup
                className={`dinner-suggestions-popup-modal dinner-suggestions-popup-modal--${flowState.currentStep}`}
                isOpen={visible}
                onClose={() => {
                    if (declineModalIsVisible) {
                        handleDismissDeclineModal()
                        return
                    }

                    trackDinnerSuggestionsEvent({
                        action: buildDinnerSuggestionsTrackingAction(
                            UserAction.ClickOn,
                            DinnerSuggestionSubject.Close,
                        ),
                        eventPage: trackingEventPage,
                    })
                    handleClose()
                }}
                onGoBack={canGoBack ? handleGoBack : undefined}
                drawerSize="tall"
                isHeaderActionsOverflow={flowState.currentStep === "intro"}
                title={headerTitle}
                isTitleHidden={!!headerTitle}
                hideCloseButton={!showHeaderCloseButton}
            >
                <div className="dinner-suggestions-popup">
                    {renderStep()}
                    {submitError ? (
                        <Alert
                            show
                            type="error"
                            message={submitError}
                        />
                    ) : null}
                    <PopupButtons isSimple>
                        {isInfoOnlyFlow ? (
                            <Button
                                onClick={() => {
                                    trackDinnerSuggestionsEvent({
                                        action: buildDinnerSuggestionsTrackingAction(
                                            UserAction.ClickOn,
                                            DinnerSuggestionSubject.Close,
                                        ),
                                        eventPage: trackingEventPage,
                                    })
                                    handleClose()
                                }}
                                text="Lukk"
                            />
                        ) : (
                            <>
                                <Button
                                    onClick={handlePrimaryAction}
                                    text={primaryButtonText}
                                    isDisabled={
                                        !canContinue ||
                                        flowState.submitting ||
                                        declineModalIsVisible ||
                                        isConsentStepPending
                                    }
                                />
                                {flowState.currentStep !== "consent" ? (
                                    <Button
                                        onClick={handleSecondaryAction}
                                        text={secondaryButtonText}
                                        isDisabled={declineModalIsVisible}
                                        isCancel
                                    />
                                ) : null}
                            </>
                        )}
                    </PopupButtons>
                </div>
            </DrawerPopup>
            {declineModalIsVisible && confirmationHost
                ? createPortal(
                      <div
                          className="dinner-suggestions-decline-popup__overlay"
                          onClick={event => {
                              if (event.target === event.currentTarget) handleDismissDeclineModal()
                          }}
                      >
                          <div
                              className="dinner-suggestions-decline-popup__dialog"
                              role="alertdialog"
                              aria-modal="true"
                              aria-labelledby="dinner-suggestions-decline-title"
                              aria-describedby="dinner-suggestions-decline-description"
                          >
                              <CloseButton
                                  className="dinner-suggestions-decline-popup__close"
                                  onClick={handleDismissDeclineModal}
                              />
                              <h2
                                  className="dinner-suggestions-decline-popup__title"
                                  id="dinner-suggestions-decline-title"
                              >
                                  Obs!
                              </h2>
                              <div id="dinner-suggestions-decline-description">
                                  <ModalBodyText>
                                      Uten samtykke kan vi ikke tilpasse oppskrifter for deg, men du
                                      kan når som helst oppdatere samtykket ditt.
                                  </ModalBodyText>
                              </div>
                              <ButtonGroup
                                  className="dinner-suggestions-decline-popup__buttons"
                                  direction="vertical"
                                  alignItems="stretch"
                              >
                                  <Button
                                      text="Gå til oppskrifter"
                                      onClick={handleContinueWithoutConsent}
                                      isDisabled={flowState.submitting}
                                      className="ngr-modal__button"
                                  />
                                  <Button
                                      text="Avbryt"
                                      onClick={handleDismissDeclineModal}
                                      isDisabled={flowState.submitting}
                                      className="ngr-modal__button"
                                  />
                              </ButtonGroup>
                          </div>
                      </div>,
                      confirmationHost,
                  )
                : null}
        </>
    )
}

export default DinnerSuggestionsPopup
